import { DebugUtils } from "abweb/utils/DebugUtils";

/**
 * Assert methods that can be used to document/verify expectations about program logic.
 * To maintain the spirit of assertions, write code as if asserts are actually no-ops.
 * An assertion error is a developer (logic) error.
 * Will throw when run within abinitio.com, and just LOG in production (which will show up in PFS)
 * In either case, the call stack will be captured.
 */
export class Assert {
  /**
   * Throw an exception if the specified condition is false.  At one point this method tried to detect whether
   * it was being run at Ab Initio or not, and only threw an exception if it thought it was.  Otherwise it would
   * return the condition value and log a message.  After some discussion among coaches we changed this to consistently
   * throw an exception and have no return value for two reasons.  First, the detection of whether it was running at
   * Ab Initio was imperfect, and we discovered that people in-house who were testing our products were in some cases
   * running into cases where an assertion was failing but were not getting any indication of that, which was
   * unacceptably lowering our product quality.  Second, we decided that in many cases if an assertion failed but
   * was merely logged, odd and difficult-to-diagnose problems might occur in the customer deployments, and that in
   * the interest of supportability it was better for the customer to get an error dialog that they could report to
   * us and that we could diagnose and fix.
   */
  static assert(condition, additionalMessage = null) {
    if (!condition) {
      throw new Error(`Assert Failure: ${additionalMessage}`); //`
    }
  }

  /**
   * AFTER SOME DISCUSSION AMONG COACHES WE DECIDED THAT THIS FUNCTION IS RISKY AND ITS USE SHOULD BE LIMITED.
   * THE ASPECT WHERE IT CONDITIONALLY THROWS AN ERROR BASED ON A GUESS OF WHERE THE CODE IS BEING RUN
   * MEANS THAT BEHAVIOR WHEN WE TEST HERE MAY BE DIFFERENT THAN WHAT CUSTOMERS SEE IN WAYS THAT MAKE IT HARD
   * TO DIAGNOSE CUSTOMER PROBLEMS.  IF YOURE TEMPTED TO CALL THIS PLEASE CONSIDER DOING IT A DIFFERENT WAY THAT
   * WILL ALWAYS BEHAVE THE SAME EVERYWHERE.  WE KNOW THAT SOME PEOPLE BELIEVE IT IS A GOOD THING TO HIDE THE ERRORS
   * AT CUSTOMER SITES BUT WE'VE DECIDED THAT WE PREFER THE DIAGNOSABILITY THAT COMES WITH THE ERRORS BEING THROWN
   * CONSISTENTLY FOR EVERYONE.
   *
   * Check an expected condition and return the value of the condition.
   * During development (specifically, when the URL contains localhost or abinitio.com), this method will
   * THROW an exception if the condition is false, but otherwise it will simply log the message and continue on
   * (thus if caller wants to abort if condition is false, then they need to conditionally return when this assert
   * returns false).
   *
   * @return the value of the condition (useful for flow control)
   */
  static developerAssertContinue(condition, additionalMessage = null) {
    if (!condition) {
      DebugUtils.developerError(`Assert Failure: ${additionalMessage}`); //`
    }
    return condition;
  }

  /**
   * Function to indicate unfinished work.
   * Intended to aid development, of course.
   * This call is breakpoint-able, a quality that a comment can't provide;
   * It's also find-usages-able, which is also handy during development.
   */
  static TODO(additionalMessage = null) {
    DebugUtils.developerError(`TODO: ${additionalMessage}`); //`
  }

  /**
   * Pass a bunch of values, and this will assert that they're all non-null and non-undefined.
   * Very useful for compactly asserting that a function's arguments are valid.
   * eg. Assert.noNulls(arg1, arg2, arg3); vs three separate lines of Asserts.
   *
   * This does not alter the path of execution; no throwing.
   */
  static noNulls(...values) {
    let nullCount = 0;
    for (let i = 0; i < values.length; i++) {
      if (values[i] === null || values[i] === undefined) {
        nullCount++;
      }
    }
    Assert.assert(nullCount === 0, "Expected non-null non-undefined values");
  }

  /**
   * Use to document that a branch should be treated as unreachable.
   * This also uses the 'never' return type, which allows for extra type inferencing:
   *
   * for example:
   *    if (this.rpcProxy == null)
   *      return Assert.unexpected();
   *    ...
   *    ExpressitAppServices.documentServices; // compiler knows that this.rpcProxy isn't null
   *
   * For more info about the never keyword, search for "typescript never".
   * Or ask: Jeff Hoel or Roy Procops
   *
   */
  static unexpected(additionalMessage = null) {
    throw new Error(`Unreachable code reached: ${additionalMessage}`); //`
  }

  /**
   * Use to document that a block of code is expected to never throw an exception.
   * If the block violates this expectation at runtime, this fact is logged so it can be debugged.
   * Program execution continues as it would if this did no catching: the exception is rethrown, not swallowed.
   */
  static noThrow(fun) {
    try {
      fun();
    } catch (e) {
      Assert.unexpected();
      throw e;
    }
  }

  /**
   * Use in the default block of a switch statement over an enum value to catch AT BUILD TIME
   * when the switch cases do not exhaustively cover ALL values of the enum.
   */
  static assertNever(x) {
    throw new Error(`Unexpected 'never' object reached: '${x}'`);
  }
}
//# sourceMappingURL=Assert.js.map
